import React, { ButtonHTMLAttributes, FC } from 'react';
import { animated, config, useSpring } from '@react-spring/web';
import { t } from 'ttag';
import styled, { keyframes } from '@xstyled/styled-components';
import PlayIcon from '~/assets/icons/play-btn-eds.svg';
import PauseIcon from '~/assets/icons/pause-btn-eds.svg';
import SpeakerIcon from '~/training/onboarding/assets/speaker.svg';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '~/training/state';

/** Soft primary halo — Figma 378:20916 pulsate attention cue */
const pulseHalo = keyframes`
  0%, 100% {
    opacity: 0.35;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.08);
  }
`;

const Button = styled.button`
  position: relative;
  background: ${p => p.theme.colors.accentDark};
  box-sizing: border-box;
  width: 46px;
  height: 46px;
  min-width: 46px;
  min-height: 46px;
  border-radius: md;
  border: none;
  /* Single cell so Play + PulseHalo + Progress ring share the same center */
  display: grid;
  grid-template: 1fr / 1fr;
  place-items: center;
  padding: 0;
  margin: 0;
  cursor: pointer;
  overflow: visible;
  font-size: 0;
  line-height: 0;
  color: var(--color-on-accent);
  border: 1px solid var(--color-accent-edge);
  transition: background-color 0.15s ease;

  & > * {
    grid-area: 1 / 1;
  }

  &:hover:not(:disabled) {
    background-color: color-mix(in srgb, var(--color-accent-dk) 70%, white);
  }

  &:active:not(:disabled) {
    background-color: color-mix(in srgb, var(--color-accent-dk) 70%, black);
  }

  &:focus {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  &:focus::after {
    content: '';
    display: block;
    position: absolute;
    border-radius: md;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid var(--color-focus-ring-inner);
    pointer-events: none;
  }

  /* @media ((max-width: 580px) and (max-height: 300px)) {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
  } */
`;

const Play = styled(PlayIcon)`
  display: block;
  width: 24px;
  height: 24px;
  max-width: 100%;
  max-height: 100%;
  z-index: 1;
  color: var(--color-on-accent);

  /* @media ((max-width: 580px) and (max-height: 300px)) {
    width: 16px;
    height: 16px;
  } */
`;

const Pause = styled(PauseIcon)`
  display: block;
  width: 24px;
  height: 24px;
  max-width: 100%;
  max-height: 100%;
  z-index: 1;
  color: var(--color-on-accent);

  /* @media ((max-width: 580px) and (max-height: 300px)) {
    width: 16px;
    height: 16px;
  } */
`;

const Speaker = styled(SpeakerIcon)`
  max-width: 24px;
  max-height: 24px;
  fill: var(--color-on-accent);
  color: var(--color-on-accent);
  z-index: 1;
`;

const PulseHalo = styled.div`
  position: absolute;
  inset: -5px;
  border-radius: 13px;
  border: 5px solid rgba(151, 56, 255, 0.5);
  pointer-events: none;
  animation: ${pulseHalo} 1.6s ease-in-out infinite;

  /* @media ((max-width: 580px) and (max-height: 300px)) {
    inset: -3px;
    border-width: 3px;
    border-radius: 10px;
  } */
`;

const Progress = animated(styled.svg<{ progress?: number }>`
  width: 46px;
  height: 46px;
  position: absolute;
  top: 0;
  left: 0;
  overflow: visible;
  stroke-width: 2px;
  fill: none;
  /* Same token as Play/Pause icons — white brand → black ring (CAT-1261). */
  stroke: var(--color-on-accent);
  stroke-linecap: round;
  stroke-linejoin: round;
  pointer-events: none;
  visibility: ${p => (p.progress ? 'visible' : 'hidden')};

  /* @media ((max-width: 580px) and (max-height: 300px)) {
    display: none;
  } */
`);

const titles = {
  playing: t`Pause`,
  ready: t`Play`,
  paused: t`Play`,
  speaker: t`Play`
};

const arialabels = {
  playing: t`Pause the instructions`,
  ready: t`Play the instructions`,
  paused: t`Play the instructions`,
  speaker: t`Play the instructions`
};

const selector = (s: TrainingState) => select(s, 'visualFeedback');

/** Progress ring: r=18 + 2px stroke → visual 38×38 inside 46×46 button */
const PROGRESS_RADIUS = 18;
const PROGRESS_CIRCUMFERENCE = 2 * Math.PI * PROGRESS_RADIUS;

const PlayIconWithFeedback = () => {
  const { visualFeedback } = useTrainingStore(selector);
  return (
    <>
      <Play id="playBtnSvg" aria-hidden="true" focusable="false" />
      {visualFeedback && <PulseHalo aria-hidden="true" />}
    </>
  );
};

export type PlayButtonEdsState = 'playing' | 'paused' | 'ready' | 'speaker';

export const PlayButtonEds: FC<
  ButtonHTMLAttributes<HTMLButtonElement> & {
    state: PlayButtonEdsState;
    progress?: number;
  }
> = ({ children: _children, state, progress, ...props }) => {
  const { x } = useSpring({
    from: { x: 0 },
    x: progress || 0,
    delay: 200,
    config: config.molasses
  });

  return (
    <Button {...props} title={titles[state]} aria-label={arialabels[state]}>
      {(state === 'paused' || state === 'ready') && (
        <PlayIconWithFeedback key={state} />
      )}
      {state === 'playing' && <Pause aria-hidden="true" focusable="false" />}
      {state === 'speaker' && <Speaker aria-hidden="true" focusable="false" />}
      {state === 'playing' && (
        <Progress
          progress={progress}
          strokeDasharray={PROGRESS_CIRCUMFERENCE}
          strokeDashoffset={x.to(v => (1 - v) * PROGRESS_CIRCUMFERENCE)}
          className="progressSvg"
          aria-hidden="true"
        >
          <circle cx="23" cy="23" r={PROGRESS_RADIUS} />
        </Progress>
      )}
    </Button>
  );
};
